import React, { useEffect, useState } from "react";
import {
  WalletCards,
  FileText,
  Download,
  Eye,
  Loader2,
  AlertCircle,
  PoundSterling,
  TrendingUp,
  Filter,
  X,
} from "lucide-react";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import useEmployeeProfile from "../../hooks/useEmployeeProfile";
import { payslipService } from "../../api/payslipService";
import timesheetService from "../../api/services/timesheetService";
import formatDate from "../../utils/formatDate";

const PayslipAndBenefits = () => {
  const { setHeader } = usePageHeader();
  const { employeeNo, isLoading: profileLoading } = useEmployeeProfile();

  // Tab state
  const [activeTab, setActiveTab] = useState("compensation");

  // Salary Slip state
  const [payrollLines, setPayrollLines] = useState([]);
  const [salarySlipLoading, setSalarySlipLoading] = useState(false);
  const [salarySlipError, setSalarySlipError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const [previewModal, setPreviewModal] = useState({
    open: false,
    pdfUrl: null,
    documentNo: null,
    employeeNo: null,
  });

  // Fiscal Year state
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState("");
  const [fiscalYearsLoading, setFiscalYearsLoading] = useState(false);

  // Compensation state
  const [payrollElements, setPayrollElements] = useState([]);
  const [compensationLoading, setCompensationLoading] = useState(false);
  const [compensationError, setCompensationError] = useState(null);

  useEffect(() => {
    setHeader({
      icon: WalletCards,
      title: "Payslip and Benefits",
      subtitle: "View your benefits and download payslips.",
    });
    return () => setHeader({ icon: null, title: "", subtitle: "" });
  }, [setHeader]);

  // Fetch compensation data when tab is active
  useEffect(() => {
    if (
      activeTab === "compensation" &&
      employeeNo &&
      payrollElements.length === 0
    ) {
      fetchCompensationData();
    }
  }, [activeTab, employeeNo]);

  // Fetch fiscal years when salary slip tab is active
  useEffect(() => {
    if (activeTab === "salarySlip" && fiscalYears.length === 0) {
      fetchFiscalYears();
    }
  }, [activeTab]);

  // Fetch salary slip data when fiscal year is selected
  useEffect(() => {
    if (activeTab === "salarySlip" && employeeNo && selectedFiscalYear) {
      fetchSalarySlipData();
    }
  }, [activeTab, employeeNo, selectedFiscalYear]);

  const fetchFiscalYears = async () => {
    setFiscalYearsLoading(true);
    try {
      const response = await timesheetService.getFiscalYears();
      const years = response.data.value || [];
      setFiscalYears(years);

      // Set default to the active fiscal year
      const activeYear = years.find((year) => year.active === true);
      if (activeYear) {
        setSelectedFiscalYear(activeYear.fiscalYear);
      } else if (years.length > 0) {
        setSelectedFiscalYear(years[0].fiscalYear);
      }
    } catch (err) {
      console.error("Failed to fetch fiscal years:", err);
      setSalarySlipError("Failed to load fiscal years.");
    } finally {
      setFiscalYearsLoading(false);
    }
  };

  const handleFiscalYearChange = (e) => {
    setSelectedFiscalYear(e.target.value);
    setPayrollLines([]); // Clear existing data when fiscal year changes
  };

  const fetchCompensationData = async () => {
    if (!employeeNo) return;

    setCompensationLoading(true);
    setCompensationError(null);

    try {
      console.log("📋 Fetching payroll elements for employee:", employeeNo);
      const elements =
        await payslipService.getEmployeePayrollElements(employeeNo);
      console.log("✅ Received payroll elements:", elements);
      const sorted = (Array.isArray(elements) ? elements : [elements])
        .slice()
        .sort((a, b) => (b.amount ?? 0) - (a.amount ?? 0));
      setPayrollElements(sorted);
    } catch (err) {
      console.error("❌ Error fetching payroll elements:", err);
      setCompensationError(err.message || "Failed to load compensation data");
    } finally {
      setCompensationLoading(false);
    }
  };

  const fetchSalarySlipData = async () => {
    if (!employeeNo || !selectedFiscalYear) return;

    setSalarySlipLoading(true);
    setSalarySlipError(null);

    try {
      console.log(
        "📋 Fetching posted payroll lines for employee:",
        employeeNo,
        "fiscal year:",
        selectedFiscalYear,
      );
      const lines = await payslipService.getPostedPayrollLines(null, {
        employeeNo: employeeNo,
        fiscalYear: selectedFiscalYear,
        $top: 50,
      });

      console.log("✅ Received payroll lines:", lines);
      const filtered = Array.isArray(lines)
        ? lines
            .filter((l) => l.netPay !== 0)
            .sort((a, b) => new Date(b.postingDate) - new Date(a.postingDate))
        : [];
      setPayrollLines(filtered);
    } catch (err) {
      console.error("❌ Error fetching payroll lines:", err);
      setSalarySlipError(err.message || "Failed to load payroll data");
    } finally {
      setSalarySlipLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "-";
    if (amount === 0) return "£0.00";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  // Format period label e.g. "April 2025 - Period 1"
  const formatPeriodLabel = (periodStartDate, payCyclePeriod) => {
    if (!periodStartDate) return `Period ${payCyclePeriod}`;
    const date = new Date(periodStartDate);
    const month = date.toLocaleString("en-GB", { month: "long" });
    const year = date.getFullYear();
    return `${month} ${year} - Period ${payCyclePeriod}`;
  };

  // Handle payslip download
  const handleDownloadPayslip = async (documentNo, empNo, payPeriod) => {
    if (!documentNo || !empNo) return;

    setDownloadingId(payPeriod);
    setSalarySlipError(null);

    try {
      console.log(
        `📥 Downloading payslip for ${empNo}, documentNo ${documentNo}`,
      );
      const result = await payslipService.generatePayslipBase64(
        documentNo,
        empNo,
      );

      if (result.value) {
        payslipService.downloadPayslipPDF(
          result.value,
          `Payslip-${empNo}-Period-${payPeriod || documentNo}.pdf`,
        );
        console.log("✅ Payslip downloaded successfully");
      } else {
        throw new Error("No payslip data received");
      }
    } catch (err) {
      console.error("❌ Error downloading payslip:", err);
      setSalarySlipError(`Failed to download payslip: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  // Handle payslip preview — opens modal with embedded PDF
  const handlePreviewPayslip = async (
    documentNo,
    empNo,
    payPeriod,
    payCyclePeriod,
  ) => {
    if (!documentNo || !empNo) return;

    setPreviewingId(payPeriod);
    setSalarySlipError(null);

    try {
      console.log(
        `👁 Previewing payslip for ${empNo}, documentNo ${documentNo}`,
      );
      const result = await payslipService.generatePayslipBase64(
        documentNo,
        empNo,
      );

      if (result.value) {
        // Revoke any previous blob URL
        if (previewModal.pdfUrl) URL.revokeObjectURL(previewModal.pdfUrl);

        const base64 = result.value.replace(
          /^data:application\/pdf;base64,/,
          "",
        );
        const byteCharacters = atob(base64);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteArray[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(blob);

        setPreviewModal({
          open: true,
          pdfUrl,
          documentNo,
          employeeNo: empNo,
          payCyclePeriod,
        });
      } else {
        throw new Error("No payslip data received");
      }
    } catch (err) {
      console.error("❌ Error previewing payslip:", err);
      setSalarySlipError(`Failed to preview payslip: ${err.message}`);
    } finally {
      setPreviewingId(null);
    }
  };

  const closePreviewModal = () => {
    if (previewModal.pdfUrl) URL.revokeObjectURL(previewModal.pdfUrl);
    setPreviewModal({
      open: false,
      pdfUrl: null,
      documentNo: null,
      employeeNo: null,
    });
  };

  // Get subtype color
  const getSubtypeStyle = (subtype) => {
    const type = (subtype || "").toLowerCase();
    if (
      type.includes("earning") ||
      type.includes("basic") ||
      type.includes("bonus")
    ) {
      return "bg-green-100 text-green-800 border-green-200";
    }
    if (type.includes("deduction") || type.includes("pension")) {
      return "bg-red-100 text-red-800 border-red-200";
    }
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  if (profileLoading) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            <span className="ml-3 text-gray-600">
              Loading employee information...
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8">
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("compensation")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "compensation"
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Pay and Benefits
                </div>
              </button>
              <button
                onClick={() => setActiveTab("salarySlip")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "salarySlip"
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Pay Slip
                </div>
              </button>
            </nav>
          </div>

          {/* Compensation Tab Content */}
          {activeTab === "compensation" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <PoundSterling className="w-5 h-5 text-indigo-600" />
                  My Pay and Benefits
                </h2>
                {employeeNo && (
                  <span className="text-sm text-gray-600">
                    Employee:{" "}
                    <span className="font-semibold">{employeeNo}</span>
                  </span>
                )}
              </div>

              {compensationError && (
                <div className="mb-4 flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-800">{compensationError}</p>
                  <button
                    onClick={() => setCompensationError(null)}
                    className="ml-auto text-red-600 hover:text-red-800 text-xl"
                  >
                    ×
                  </button>
                </div>
              )}

              {compensationLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  <span className="ml-3 text-gray-600">
                    Loading compensation data...
                  </span>
                </div>
              ) : payrollElements.length === 0 ? (
                <div className="text-center py-12">
                  <PoundSterling className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-600 mb-2">
                    No Compensation Data Available
                  </p>
                  <p className="text-sm text-gray-500">
                    Your compensation details will appear here once they are set
                    up in Business Central
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Code
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subtype
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Pay Unit
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Annual Salary
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Year to Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payrollElements.map((element, index) => (
                        <tr
                          key={`${element.code}-${index}`}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {element.code || "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full border ${getSubtypeStyle(
                                element.type,
                              )}`}
                            >
                              {element.type || "-"}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {element.subtype?.trim() || "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {element.payUnit || "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                            {formatCurrency(element.amount)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {element.annualSalary > 0
                              ? formatCurrency(element.annualSalary)
                              : "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                            {element.yeartoDate > 0
                              ? formatCurrency(element.yeartoDate)
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Salary Slip Tab Content */}
          {activeTab === "salarySlip" && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  My Payslips
                </h2>
                {employeeNo && (
                  <span className="text-sm text-gray-600">
                    Employee:{" "}
                    <span className="font-semibold">{employeeNo}</span>
                  </span>
                )}
              </div>

              {/* Fiscal Year Filter */}
              <div className="mb-6 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-gray-500" />
                  <label
                    htmlFor="salarySlipFiscalYear"
                    className="text-sm font-medium text-gray-700"
                  >
                    Financial Year:
                  </label>
                </div>
                <select
                  id="salarySlipFiscalYear"
                  value={selectedFiscalYear}
                  onChange={handleFiscalYearChange}
                  disabled={fiscalYearsLoading}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {fiscalYearsLoading ? (
                    <option value="">Loading...</option>
                  ) : fiscalYears.length === 0 ? (
                    <option value="">No financial years available</option>
                  ) : (
                    fiscalYears.map((year) => (
                      <option key={year.fiscalYear} value={year.fiscalYear}>
                        {year.fiscalYear}
                        {year.active ? " (Active)" : ""}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {salarySlipError && (
                <div className="mb-4 flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-800">{salarySlipError}</p>
                  <button
                    onClick={() => setSalarySlipError(null)}
                    className="ml-auto text-red-600 hover:text-red-800 text-xl"
                  >
                    ×
                  </button>
                </div>
              )}

              {fiscalYearsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  <span className="ml-3 text-gray-600">
                    Loading fiscal years...
                  </span>
                </div>
              ) : salarySlipLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  <span className="ml-3 text-gray-600">
                    Loading payroll data...
                  </span>
                </div>
              ) : payrollLines.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-600 mb-2">
                    No Payslips Available
                  </p>
                  <p className="text-sm text-gray-500">
                    No payslips found for the selected fiscal year
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Period
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Pay Date
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Gross Pay
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Tax
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Net Pay
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payrollLines.map((line) => {
                        const isDownloading = downloadingId === line.documentNo;
                        const isPreviewing = previewingId === line.documentNo;
                        const isBusy = isDownloading || isPreviewing;
                        return (
                          <tr
                            key={line.documentNo}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(line.periodStartDate)} –{" "}
                              {formatDate(line.periodEndDate)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                              {formatDate(line.postingDate)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                              {formatCurrency(line.grossPay)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-red-600 text-right">
                              {line.taxForPeriod
                                ? formatCurrency(Math.abs(line.taxForPeriod))
                                : "-"}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-green-700 text-right">
                              {formatCurrency(line.netPay)}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() =>
                                    handlePreviewPayslip(
                                      line.documentNo,
                                      line.employeeNo,
                                      line.documentNo,
                                      line.payCyclePeriod,
                                    )
                                  }
                                  disabled={isBusy}
                                  title="Preview payslip"
                                  className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                  {isPreviewing ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Eye className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() =>
                                    handleDownloadPayslip(
                                      line.documentNo,
                                      line.employeeNo,
                                      line.payCyclePeriod,
                                    )
                                  }
                                  disabled={isBusy}
                                  title="Download payslip"
                                  className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                  {isDownloading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PDF Preview Modal */}
      {previewModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div
            className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-5xl flex flex-col"
            style={{ height: "90vh" }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">
                Document Preview
              </h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={() =>
                    handleDownloadPayslip(
                      previewModal.documentNo,
                      previewModal.employeeNo,
                      previewModal.payCyclePeriod,
                    )
                  }
                  disabled={downloadingId === previewModal.documentNo}
                  className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold"
                >
                  {downloadingId === previewModal.documentNo ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Download
                </button>
                <button
                  onClick={closePreviewModal}
                  className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* PDF iframe */}
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe
                src={previewModal.pdfUrl}
                className="w-full h-full"
                title="Payslip Preview"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PayslipAndBenefits;
